import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router";
import { AppShell } from "../components/app-shell.js";
import { useConfirm } from "../features/canvas-kit/ui/confirm-dialog.jsx";
import { TacticsEditor } from "../features/tactics/editor.jsx";
import type { BoardSnapshot } from "../features/tactics/scene.js";
import { useSync } from "../providers/sync-provider";
import {
  type BoardRow,
  deleteBoard,
  listBoards,
  parseSnapshot,
  updateBoard,
} from "../repos/boards";

/**
 * Editor route — `/tactics/:boardId`.
 *
 * Loads a single board from the local SQLite cache (kept fresh by the sync
 * engine) and mounts the heavy Konva editor. Missing/unknown ids redirect
 * back to the list so deep links degrade gracefully.
 */
export function TacticsEditorPage() {
  const { boardId } = useParams<{ boardId: string }>();
  const navigate = useNavigate();
  const { db, orgId, userId, engine } = useSync();
  const qc = useQueryClient();
  const ready = Boolean(db && orgId && userId);

  const boards = useQuery<BoardRow[]>({
    queryKey: ["boards", orgId, userId],
    enabled: ready,
    queryFn: () => listBoards(db!, orgId!, userId!),
    refetchInterval: 5000,
  });

  const active = useMemo(
    () => boards.data?.find((b) => b.id === boardId) ?? null,
    [boards.data, boardId],
  );

  // Once data has loaded and the id isn't found, bounce back to the list.
  useEffect(() => {
    if (!ready) return;
    if (boards.isLoading) return;
    if (!active && boardId) navigate("/tactics", { replace: true });
  }, [ready, boards.isLoading, active, boardId, navigate]);

  const save = useMutation({
    mutationFn: async (args: { id: string; name: string; snapshot: BoardSnapshot }) => {
      if (!ready) throw new Error("Lokale Datenbank nicht bereit.");
      await updateBoard(db!, {
        id: args.id,
        name: args.name,
        snapshot: args.snapshot,
      });
      void engine?.syncNow();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["boards"], exact: false });
    },
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      if (!ready) throw new Error("Lokale Datenbank nicht bereit.");
      await deleteBoard(db!, { id, orgId: orgId! });
      void engine?.syncNow();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["boards"], exact: false });
      navigate("/tactics", { replace: true });
    },
  });

  const snap = useMemo(
    () => (active ? parseSnapshot(active.snapshot_json) : null),
    // Stable snapshot identity across periodic refetches; see whiteboard
    // route for the same pattern + rationale.
    [active?.snapshot_json],
  );

  const { confirm, dialog } = useConfirm();

  if (!active || !snap) {
    return (
      <AppShell title="Taktikboard" wide chromeless>
        <div className="flex h-[60vh] items-center justify-center text-body-sm text-muted-foreground">
          {boards.isLoading ? "Lade Spielzug…" : "Spielzug nicht gefunden."}
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell title="Taktikboard" wide chromeless>
      <TacticsEditor
        key={active.id}
        name={active.name}
        snapshot={snap}
        saving={save.isPending}
        onClose={() => navigate("/tactics")}
        onSave={({ name, snapshot }) => save.mutate({ id: active.id, name, snapshot })}
        onDelete={async () => {
          const ok = await confirm({
            title: "Spielzug löschen?",
            message: `„${active.name}“ wird unwiderruflich gelöscht.`,
            confirmLabel: "Löschen",
          });
          if (ok) remove.mutate(active.id);
        }}
      />
      {dialog}
    </AppShell>
  );
}
